import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faGraduationCap, faVideo, faCheckCircle,
    faPlayCircle, faClock, faCompass, faPlay, faListOl, faBook
} from '@fortawesome/free-solid-svg-icons';
import { useLocation } from 'react-router-dom';
import TrainingVideoPlayer from './TrainingVideoPlayer';
import { useFlashMessage } from './FlashMessage';
import axiosInstance from '../utils/axiosConfig';
import Footer from './ui/Footer';
import HMSNavbarWrapper from './ui/HMSNavbarWrapper';
import HMSButton from './ui/HMSButton';
import HMSEmptyState from './ui/HMSEmptyState';
import HMSPageHeader from './ui/HMSPageHeader';
import HMSSkeleton from './ui/HMSSkeleton';
import HamsterFloatingButton from './HamsterFloatingButton';
import BackToTopButton from './BackToTopButton';
import HamsterGlossary from './HamsterGlossary';
import { useWalkthrough } from '../contexts/WalkthroughContext';
import { allTours } from '../walkthroughs';
import './HamsterTraining.css';

// Synthetic tab id for the interactive walkthroughs section. Kept out of
// the category response so the backend stays untouched. The string must
// not collide with any TrainingCategory.slug.
const WALKTHROUGHS_TAB_ID = '__walkthroughs__';

// Synthetic tab id for the IMT Lexicon glossary. Self-contained component with
// its own search/filters and admin CRUD; must not collide with a category slug.
const GLOSSARY_TAB_ID = '__glossary__';

const HamsterTraining = () => {
    const { showSuccess, showError } = useFlashMessage();
    const location = useLocation();
    const { startTour } = useWalkthrough();

    // State management
    const [categories, setCategories] = useState([]);
    const [activeTab, setActiveTab] = useState(null);
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [userProgress, setUserProgress] = useState({});
    const [stats, setStats] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredVideos, setFilteredVideos] = useState([]);
    
    // Debounce ref
    const searchDebounceRef = useRef(null);
    
    // Scroll to top on component mount
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);
    
    // Fetch categories and initial data
    useEffect(() => {
        const loadInitialData = async () => {
            await fetchCategories();
            await fetchStats();
        };
        loadInitialData();
    }, []);
    
    // Fetch videos when tab changes (skip the synthetic walkthroughs tab,
    // which has no backend videos).
    useEffect(() => {
        if (activeTab && activeTab !== WALKTHROUGHS_TAB_ID) {
            fetchVideos(activeTab);
        } else if (activeTab === WALKTHROUGHS_TAB_ID) {
            setLoading(false);
        }
    }, [activeTab]);
    
    // Handle URL parameters for direct video access from HAMSTER AI
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const videoId = params.get('videoId');
        const autoplay = params.get('autoplay');
        
        if (videoId && autoplay === 'true') {
            // Fetch the specific video by ID
            const loadVideoById = async () => {
                try {
                    const response = await axiosInstance.get(`/api/training/videos/${videoId}/`);
                    const videoData = response.data;
                    
                    // Set this video as selected (will trigger autoplay in TrainingVideoPlayer)
                    setSelectedVideo(videoData);
                    
                    // Set the active tab to this video's category
                    if (videoData.category) {
                        setActiveTab(videoData.category);
                    }
                    
                    console.log('Auto-playing video from HAMSTER AI:', videoData.title);
                } catch (error) {
                    console.error('Error loading video by ID:', error);
                    showError('Video not found');
                }
            };
            
            loadVideoById();
        }
    }, [location.search]);
    
    const fetchCategories = async () => {
        try {
            const response = await axiosInstance.get('/api/training/categories/');
            setCategories(response.data);
            // Always default to the synthetic Interactive Walkthroughs tab
            // so users land on the guided tours first. Other tabs remain
            // accessible via the tab strip.
            setActiveTab(WALKTHROUGHS_TAB_ID);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching categories:', error);
            if (error.response?.status === 403) {
                showError('You do not have permission to access training');
            } else {
                showError('Failed to load training categories');
            }
            setActiveTab(WALKTHROUGHS_TAB_ID);
            setLoading(false);
        }
    };
    
    const fetchVideos = async (categorySlug) => {
        setLoading(true);
        try {
            const url = `/api/training/videos/?category=${categorySlug}`;
            const response = await axiosInstance.get(url);
            setVideos(response.data);
            setFilteredVideos(response.data);
            
            // Build progress map
            const progressMap = {};
            response.data.forEach(video => {
                if (video.user_progress) {
                    progressMap[video.id] = video.user_progress;
                }
            });
            setUserProgress(progressMap);
        } catch (error) {
            console.error('Error fetching videos:', error);
            showError('Failed to load training videos');
        } finally {
            setLoading(false);
        }
    };
    
    const fetchStats = async () => {
        try {
            const response = await axiosInstance.get('/api/training/progress/stats/');
            setStats(response.data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };
    
    const handleVideoSelect = (video) => {
        setSelectedVideo(video);
    };
    
    const handleVideoClose = () => {
        setSelectedVideo(null);
        // Refresh videos and stats
        fetchVideos(activeTab);
        fetchStats();
    };
    
    const handleNextVideo = (currentVideoId) => {
        // Find current video index
        const currentIndex = videos.findIndex(v => v.id === currentVideoId);
        
        // Get next video (wrap to first if at end)
        if (currentIndex !== -1 && currentIndex < videos.length - 1) {
            const nextVideo = videos[currentIndex + 1];
            setSelectedVideo(nextVideo);
        } else {
            // At the end, close player
            handleVideoClose();
        }
    };
    
    const handleProgressUpdate = async (videoId, progressData) => {
        try {
            await axiosInstance.post(
                `/api/training/videos/${videoId}/update_progress/`,
                progressData
            );
            
            // Update local progress state
            setUserProgress(prev => ({
                ...prev,
                [videoId]: progressData
            }));
        } catch (error) {
            console.error('Error updating progress:', error);
        }
    };
    
    const formatDuration = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };
    
    const handleSearchChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        
        // Clear existing debounce timer
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        
        // Set new debounce timer
        searchDebounceRef.current = setTimeout(() => {
            filterVideos(query);
        }, 300); // 300ms debounce
    };
    
    const filterVideos = (query) => {
        if (!query.trim()) {
            setFilteredVideos(videos);
            return;
        }
        
        const lowercaseQuery = query.toLowerCase();
        const filtered = videos.filter(video => 
            video.title.toLowerCase().includes(lowercaseQuery)
        );
        
        setFilteredVideos(filtered);
    };
    
    const clearSearch = () => {
        setSearchQuery('');
        setFilteredVideos(videos);
        
        // Clear debounce timer
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
    };
    
    // Cleanup debounce on unmount
    useEffect(() => {
        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
    }, []);

    // Group tours by their `group` field for sectioned rendering, and apply
    // the top-bar search query against title + description so users can find
    // a walkthrough the same way they'd find a training video. Anything
    // without an explicit group falls back to "module" so the card never
    // disappears from the grid.
    const groupedTours = useMemo(() => {
        const groups = { module: [], flow: [] };
        const q = searchQuery.trim().toLowerCase();
        allTours.forEach((tour) => {
            if (q) {
                const haystack = `${tour.title || ''} ${tour.description || ''}`.toLowerCase();
                if (!haystack.includes(q)) return;
            }
            const key = tour.group === 'flow' ? 'flow' : 'module';
            groups[key].push(tour);
        });
        return groups;
    }, [searchQuery]);

    const totalMatchingTours = groupedTours.module.length + groupedTours.flow.length;

    // Tab tray for the shared page header. The synthetic walkthroughs tab
    // leads so users see the guided tours ahead of the video categories; each
    // category keys off its slug because that is what activeTab holds. No
    // counts and no completed/in-progress variants: the legacy tabs never
    // rendered either.
    const trainingTabs = [
        { key: WALKTHROUGHS_TAB_ID, label: 'Interactive Walkthroughs', icon: faCompass },
        ...categories.map((category) => ({
            key: category.slug,
            label: category.display_name,
            icon: faGraduationCap
        }))
    ];

    const renderWalkthroughCard = (tour) => (
        <div key={tour.id} className="walkthrough-card">
            <div className="walkthrough-card-icon">
                <FontAwesomeIcon icon={tour.icon || faCompass} />
            </div>
            <div className="walkthrough-card-body">
                <h3 className="walkthrough-card-title">{tour.title}</h3>
                <p className="walkthrough-card-description">
                    {tour.description}
                </p>
                {tour.steps?.length ? (
                    <span className="walkthrough-card-meta">
                        <FontAwesomeIcon icon={faListOl} />
                        {tour.steps.length} {tour.steps.length === 1 ? 'step' : 'steps'}
                    </span>
                ) : null}
            </div>
            <HMSButton
                variant="primary"
                size="compact"
                onClick={() => startTour(tour.id)}
            >
                <FontAwesomeIcon icon={faPlay} />
                Start tour
            </HMSButton>
        </div>
    );
    
    const renderVideoCard = (video) => {
        const progress = userProgress[video.id];
        const isCompleted = progress?.is_completed || false;
        const completionPercentage = progress?.completion_percentage || 0;
        
        return (
            <div 
                key={video.id}
                className="training-video-card"
                onClick={() => handleVideoSelect(video)}
            >
                {/* Thumbnail */}
                <div className="training-video-thumbnail">
                    {video.thumbnail_url ? (
                        <img src={video.thumbnail_url} alt={video.title} />
                    ) : (
                        <div className="training-video-placeholder">
                            <FontAwesomeIcon icon={faVideo} />
                        </div>
                    )}
                    
                    {/* Play overlay */}
                    <div className="training-video-overlay">
                        <FontAwesomeIcon icon={faPlayCircle} className="play-icon" />
                    </div>
                    
                    {/* Duration badge - always show */}
                    {video.duration_seconds > 0 && (
                        <div className="training-video-duration">
                            <FontAwesomeIcon icon={faClock} size="sm" />
                            {formatDuration(video.duration_seconds)}
                        </div>
                    )}
                    
                    {/* Progress bar */}
                    {completionPercentage > 0 && (
                        <div className="training-video-progress-bar">
                            <div 
                                className="training-video-progress-fill"
                                style={{ width: `${completionPercentage}%` }}
                            />
                        </div>
                    )}
                </div>
                
                {/* Content */}
                <div className="training-video-content">
                    <div className="training-video-header">
                        <h3 className="training-video-title">{video.title}</h3>
                        {isCompleted && (
                            <FontAwesomeIcon 
                                icon={faCheckCircle} 
                                className="training-video-completed-icon"
                            />
                        )}
                    </div>
                    
                    <p className="training-video-description">
                        {video.description}
                    </p>
                    
                    {completionPercentage > 0 && !isCompleted && (
                        <div className="training-video-meta">
                            <span className="training-video-progress-text">
                                {completionPercentage}% watched
                            </span>
                        </div>
                    )}
                </div>
            </div>
        );
    };
    
    return (
        <HMSNavbarWrapper>
            <div className="training-layout">
                {/* Main Content */}
                <div className="training-main-content">
                {/* Shared screen header: brand block, pill tray, header widgets */}
                <HMSPageHeader
                    title="Hamster Training"
                    kicker="Workspace"
                    tabs={{ items: trainingTabs, activeKey: activeTab, onChange: setActiveTab }}
                    actions={
                        <>
                        {/* Search Element */}
                        <div className="header-search-wrapper">
                            <div className="header-search-container">
                                <div className="search-icon">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </div>
                                <input
                                    type="text"
                                    placeholder={
                                        activeTab === WALKTHROUGHS_TAB_ID
                                            ? 'Search walkthroughs...'
                                            : 'Search training videos...'
                                    }
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    className="header-search-input"
                                />
                                {searchQuery && (
                                    <button
                                        type="button"
                                        className="clear-search"
                                        onClick={clearSearch}
                                        aria-label="Clear search"
                                    >
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Stats - Compact Card. Hidden on the Interactive
                            Walkthroughs tab since those tours are not tracked
                            by the video progress backend. */}
                        {stats && activeTab !== WALKTHROUGHS_TAB_ID && (
                            <div className="training-stats">
                                <div className="compact-training-card">
                                    <div className="compact-training-label">Training Progress Overview</div>
                                    <div className="compact-training-progress-bar">
                                        {/* Calculate total videos */}
                                        {(() => {
                                            const totalVideos = stats.completed_videos + stats.in_progress_videos + (stats.total_videos || (stats.completed_videos + stats.in_progress_videos));
                                            const completedPercent = (stats.completed_videos / totalVideos) * 100;
                                            const inProgressPercent = (stats.in_progress_videos / totalVideos) * 100;
                                            const remainingPercent = 100 - completedPercent - inProgressPercent;
                                        
                                            return (
                                                <>
                                                    {/* Completed videos (green) */}
                                                    <div 
                                                        className="compact-training-progress-completed"
                                                        style={{ width: `${completedPercent}%` }}
                                                    ></div>
                                                    {/* In progress videos (yellow) */}
                                                    <div 
                                                        className="compact-training-progress-in-progress"
                                                        style={{ 
                                                            left: `${completedPercent}%`,
                                                            width: `${inProgressPercent}%` 
                                                        }}
                                                    ></div>
                                                    {/* Remaining videos (gray) */}
                                                    <div 
                                                        className="compact-training-progress-remaining"
                                                        style={{ 
                                                            left: `${completedPercent + inProgressPercent}%`,
                                                            width: `${remainingPercent}%` 
                                                        }}
                                                    ></div>
                                                </>
                                            );
                                        })()}
                                    </div>
                                    <div className="compact-training-breakdown">
                                        <div className="compact-training-stat">
                                            <div className="compact-training-stat-label">Completed</div>
                                            <div className="compact-training-stat-value completed">
                                                {stats.completed_videos}
                                            </div>
                                        </div>
                                        <div className="compact-training-stat">
                                            <div className="compact-training-stat-label">In Progress</div>
                                            <div className="compact-training-stat-value in-progress">
                                                {stats.in_progress_videos}
                                            </div>
                                        </div>
                                        <div className="compact-training-stat">
                                            <div className="compact-training-stat-label">Progress</div>
                                            <div className="compact-training-stat-value percentage">
                                                {Math.round(stats.completion_rate)}%
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        </>
                    }
                />

                {/* Content Area */}
                <div className="training-content">
                    {activeTab === GLOSSARY_TAB_ID ? (
                        <HamsterGlossary />
                    ) : activeTab === WALKTHROUGHS_TAB_ID ? (
                        totalMatchingTours === 0 ? (
                            <HMSEmptyState
                                icon={<FontAwesomeIcon icon={faCompass} />}
                                title="No walkthroughs found"
                                hint={`No walkthroughs match "${searchQuery}".`}
                            />
                        ) : (
                            <div className="walkthroughs-sections">
                                {groupedTours.module.length > 0 && (
                                    <section className="walkthroughs-section">
                                        <div className="walkthroughs-section-heading">
                                            Module overview
                                        </div>
                                        <div className="walkthroughs-grid">
                                            {groupedTours.module.map(renderWalkthroughCard)}
                                        </div>
                                    </section>
                                )}
                                {groupedTours.flow.length > 0 && (
                                    <section className="walkthroughs-section">
                                        <div className="walkthroughs-section-heading">
                                            Per-flow deep dive
                                        </div>
                                        <div className="walkthroughs-grid">
                                            {groupedTours.flow.map(renderWalkthroughCard)}
                                        </div>
                                    </section>
                                )}
                            </div>
                        )
                    ) : loading ? (
                        /* Three blocks in the real video grid, at roughly the
                           height a card settles on, so the grid does not jump
                           when the videos land. */
                        <div className="training-video-grid">
                            <HMSSkeleton shape="rect" height={280} />
                            <HMSSkeleton shape="rect" height={280} />
                            <HMSSkeleton shape="rect" height={280} />
                        </div>
                    ) : filteredVideos.length === 0 ? (
                        <HMSEmptyState
                            icon={<FontAwesomeIcon icon={faVideo} />}
                            title={searchQuery ? 'No videos found' : 'No Training Videos Available'}
                            hint={searchQuery ? `No videos match "${searchQuery}"` : 'Training content will be added soon.'}
                        />
                    ) : (
                        <div className="training-video-grid">
                            {filteredVideos.map(renderVideoCard)}
                        </div>
                    )}
                </div>
            </div>
            
            {/* Video Player Modal */}
            {selectedVideo && (
                <TrainingVideoPlayer
                    video={selectedVideo}
                    videos={videos}
                    progress={userProgress[selectedVideo.id]}
                    onClose={handleVideoClose}
                    onProgressUpdate={handleProgressUpdate}
                    onNextVideo={handleNextVideo}
                />
            )}
            
                {/* Hamster Chat Integration */}
                <HamsterFloatingButton />
            
                {/* Back to Top Button */}
                <BackToTopButton />
            
                {/* Footer */}
                <Footer />
            </div>
        </HMSNavbarWrapper>
    );
};

export default HamsterTraining;

